import exceljs from "exceljs";
import { saveAs } from "file-saver";

import { useAuthStore } from "src/components/main/authStore";
import { useUiStore } from "src/components/main/uiStore";
import {
    mimeTypeMap,
    MyDate,
    onlyUnique,
    simpleStatusColorCodExcelHex,
    defaultEmptyDateTime,
    weeknum,
    weekDays,
} from "src/components/common/functions";
import type { ProjectCalendarAssignmentStatus, ProjectCalendarRow } from "./projectCalendar";

type ProjectCalendarExportProps = {
    filteredData: ProjectCalendarRow[];
    week: string;
};

export default function Export({
    filteredData,
    week,
}: ProjectCalendarExportProps) {
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const user = useAuthStore(state => state.user);

    const exportFunc = () => {
        if (!filteredData.length) {
            window.alert("No data to export.");
            return;
        }
        setShowLoader(true);
        const mainData = filteredData
            .map(raw => raw.projectId)
            .filter(onlyUnique)
            .map(projectId => filteredData.filter(raw => weeknum(raw.startTime) === week).filter(raw => raw.projectId === projectId));
        const workbook = new exceljs.Workbook();
        workbook.creator = user?.userName ?? "";
        workbook.created = new Date();
        workbook.calcProperties.fullCalcOnLoad = true;
        const headers = ["", ...weekDays, "Total"];
        for (let totalI = 0; totalI < mainData.length; totalI++) {
            const projectAssignments = mainData[totalI];
            let maxLength = 1;
            const worksheet = workbook.addWorksheet(`Project ${totalI + 1}`, { views: [{ showGridLines: false }] });
            worksheet.state = "visible";
            worksheet.properties.defaultColWidth = 22.57;
            for (let columnIndex = 1; columnIndex < 10; columnIndex++) {
                worksheet.mergeCells(1, 2 * columnIndex - 1, 1, 2 * columnIndex);
                worksheet.getCell(1, 2 * columnIndex - 1).value = headers[columnIndex - 1];
                worksheet.getCell(1, 2 * columnIndex - 1).alignment = { vertical: "middle", horizontal: "center" };
                worksheet.getCell(1, 2 * columnIndex - 1).border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            }
            for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
                const dayAssignments = projectAssignments
                    .filter(value => (new Date(value.startTime.substring(0, 10).replace(/-/g, "/"))).getDay() === (dayIndex === 6 ? 0 : dayIndex + 1))
                    .sort((a, b) => new Date(a.travelStartTime.replace(/-/g, "/")).getTime() - new Date(b.travelStartTime.replace(/-/g, "/")).getTime());
                const columnData = [headers[dayIndex + 1]];
                maxLength = Math.max(maxLength, dayAssignments.length);
                const projectTitleCell = worksheet.getCell(1, 1);
                projectTitleCell.value = `${projectAssignments[0].projectNumber} - ${projectAssignments[0].organizationName} - ${projectAssignments[0].clientProjectNumber}`;
                const maxWidth = String(projectTitleCell.value ?? "").length * (projectTitleCell.font ? (projectTitleCell.font.size ?? 11) / 11 : 1) - 18;
                worksheet.getColumn(1).width = maxWidth < 18 ? 18 : maxWidth;
                for (let rowIndex = 0; rowIndex < dayAssignments.length; rowIndex++) {
                    const userCell = worksheet.getCell(2 + rowIndex * 7, 2 * dayIndex + 3);
                    userCell.value = dayAssignments[rowIndex].userName;
                    worksheet.mergeCells(2 + rowIndex * 7, 2 * dayIndex + 3, 8 + rowIndex * 7, 2 * dayIndex + 3);
                    userCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                    userCell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                    userCell.font = {
                        color: { argb: simpleStatusColorCodExcelHex[dayAssignments[rowIndex].assignmentStatus as ProjectCalendarAssignmentStatus] },
                    };
                    columnData.push(
                        dayAssignments[rowIndex].preDriver ?? "",
                        dayAssignments[rowIndex].travelStartTime === defaultEmptyDateTime.replace(/-/g, "/") ? "" : dayAssignments[rowIndex].travelStartTime,
                        dayAssignments[rowIndex].workStartTime === defaultEmptyDateTime.replace(/-/g, "/") ? "" : dayAssignments[rowIndex].workStartTime,
                        dayAssignments[rowIndex].hadLunch ?? "",
                        dayAssignments[rowIndex].workEndTime === defaultEmptyDateTime.replace(/-/g, "/") ? "" : dayAssignments[rowIndex].workEndTime,
                        dayAssignments[rowIndex].travelEndTime === defaultEmptyDateTime.replace(/-/g, "/") ? "" : dayAssignments[rowIndex].travelEndTime,
                        dayAssignments[rowIndex].postDriver ?? "",
                    );
                }
                worksheet.getColumn(2 * dayIndex + 4).values = columnData;
            }
            const headerColumnData = [`${projectAssignments[0].projectNumber} - ${projectAssignments[0].organizationName} - ${projectAssignments[0].clientProjectNumber}`];
            for (let personIndex = 0; personIndex < maxLength; personIndex++) {
                worksheet.mergeCells(personIndex * 7 + 2, 1, personIndex * 7 + 8, 1);
                worksheet.getCell(personIndex * 7 + 2, 1).value = `Person ${personIndex + 1}`;
                worksheet.getCell(personIndex * 7 + 2, 1).alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                worksheet.getCell(personIndex * 7 + 2, 1).border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
                worksheet.getCell(personIndex * 7 + 2, 2).value = "";
                headerColumnData.push(
                    "pre driver",
                    "travel start",
                    "work start",
                    "lunch",
                    "work end",
                    "travel end",
                    "post driver",
                );
            }
            worksheet.getColumn(2).values = headerColumnData;
        }
        void workbook.xlsx.writeBuffer().then(buffer => {
            saveAs(
                new Blob([buffer], { type: mimeTypeMap.xlsx }),
                `Project Calendar export ${(new MyDate()).s()}.xlsx`
            );
            setShowLoader(false);
        });
    };

    return (
        <button type="button" className="btn btn-outline-primary" onClick={exportFunc}>
            Export
        </button>
    );
}
